import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { mapSurveyPropertiesToQuestions } from '../utils';
import { enqueuePendingCsatPrepareSurvey, enqueuePendingCsatResponseContext, getCsatProvider } from '../services';

import collectiveApi from 'modules/common/collectiveApi';

type StartResponse = {
    csat_row_id?: number;
    survey_id: string;
    question: string;
    follow_up_question: string;
    quarter?: number;
    year?: number;
    survicate?: Record<string, unknown>;
};

const requestSurvey = async (
    surveyType: string,
    featureName: string,
    featureSection: string,
    otherProperties: Record<string, unknown>
): Promise<StartResponse> =>
    collectiveApi
        .post(`/surveys/csat/survey/start/`, {
            survey_type: surveyType,
            properties: {
                feature_name: featureName,
                feature_section: featureSection,
                ...otherProperties,
            },
        })
        .then(({ data }: { data: StartResponse }) => {
            if (data.survicate && data.csat_row_id != null) {
                const context = {
                    csat_row_id: String(data.csat_row_id),
                    feature_name: featureName,
                    feature_section: featureSection,
                };
                const csatProvider = getCsatProvider();
                if (csatProvider) {
                    csatProvider.setResponseContext(context);
                    csatProvider.prepareSurvey(data.survicate);
                } else {
                    enqueuePendingCsatResponseContext(context);
                    enqueuePendingCsatPrepareSurvey(data.survicate);
                }
            }

            return data;
        });

export default function useRequestSurveyIdAndQuestions(
    surveyType: string,
    featureName: string,
    featureSection: string,
    otherProperties: Record<string, unknown>,
    enabled: boolean = true
) {
    const { isError, isLoading, data, isFetching, error, refetch } = useQuery<
        StartResponse,
        AxiosError<{ error: string }>
    >({
        queryKey: ['surveyIdAndQuestions', surveyType, featureName, featureSection],
        queryFn: () => requestSurvey(surveyType, featureName, featureSection, otherProperties),
        staleTime: Infinity,
        retryOnMount: false,
        retry: false,
        enabled,
    });
    if (isError) {
        return {
            isSuccess: false,
            isLoading: false,
            error: error?.response?.data?.error || error?.message,
        };
    }
    if (isLoading || isFetching) {
        return {
            isLoading: true,
        };
    }
    if (!data?.question) {
        return {
            isLoading: false,
            isSuccess: false,
        };
    }
    const properties = {
        question: data.question,
        follow_up_question: data.follow_up_question,
        feature_section: featureSection,
        feature_name: featureName,
        quarter: data.quarter,
        year: data.year,
        ...otherProperties,
    };
    return {
        isLoading: false,
        isSuccess: true,
        data: {
            surveyId: data.survey_id,
            csatRowId: data.csat_row_id,
            surveyProperties: properties,
            surveyQuestions: mapSurveyPropertiesToQuestions(properties),
        },
        refetch,
    };
}
